import React from "react";
import {
    Alert,
    Box,
    Button,
    Modal,
    SpaceBetween,
    Spinner,
} from "@cloudscape-design/components";
import { Trans, useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import rehypeSanitize from "rehype-sanitize";
import { useInView } from "react-intersection-observer";
import { useGetTermsOfService } from "../../hooks/useGetTermsOfService";
import { getCustomBrandingConfig, getPortalConfig } from "../../configurations";
import { ValueWithLabel } from "../toolbar/ValueWithLabel";
import { CopyButton } from "../general/CopyButton";
import { AWSError } from "aws-sdk";

// Follow server side validation: https://tiny.amazon.com/10xhg14y3
const SANITIZE_SCHEMA = {
    tagNames: [
        // Allow only basic text formatting
        "p",
        "br",
        "strong",
        "b",
        "em",
        "i",
        // Allow headings for document structure
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        // Allow lists for structured content
        "ul",
        "ol",
        "li",
        // Allow blockquotes and horizontal rules
        "blockquote",
        "hr",
    ],
    protocols: {}, // No protocols allowed (no links)
};

interface TermsOfServiceModalProps {
    onCancel: () => void;
    onProceed: () => void;
}

/**
 * Match restriction in Ermine CP markdown validator
 * https://code.amazon.com/packages/AWSErmineControlPlaneService/blobs/heads/mainline/--/src/com/amazon/awserminecontrolplaneservice/validators/branding/MarkdownValidator.java
 */
export const TermsOfServiceModal = ({
    onCancel,
    onProceed,
}: TermsOfServiceModalProps) => {
    // Intersection observer to detect when user scrolls to bottom
    const { ref: bottomRef, inView: hasScrolledToBottom } = useInView({
        threshold: 0,
        triggerOnce: true,
    });

    const portalConfig = getPortalConfig();
    const customBrandingConfig = getCustomBrandingConfig();
    const { content, isLoading, error, isError } = useGetTermsOfService(
        portalConfig.portalArn,
        customBrandingConfig?.userSettingId ?? ""
    );

    let childComponent = <></>;
    if (isLoading) {
        childComponent = (
            <SpaceBetween size="xs" direction="horizontal">
                <Spinner />
                <Trans i18nKey="termsOfService.modal.content.loading.label" />
            </SpaceBetween>
        );
    } else if (isError) {
        childComponent = <ErrorAlert error={error} />;
    } else {
        childComponent = (
            <div data-testid="tos-markdown-content">
                <ReactMarkdown
                    rehypePlugins={[[rehypeSanitize, SANITIZE_SCHEMA]]}
                    skipHtml={true}
                >
                    {content}
                </ReactMarkdown>
                <div
                    ref={bottomRef}
                    style={{ height: "1px" }}
                    data-testid="tos-bottom-trigger"
                />
            </div>
        );
    }

    return (
        <Modal
            onDismiss={onCancel}
            visible={true}
            size={"large"}
            data-testid="terms-of-service-modal"
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button onClick={onCancel}>
                            <Trans
                                i18nKey={
                                    "termsOfService.modal.cancel.button.label"
                                }
                            ></Trans>
                        </Button>
                        <Button
                            onClick={onProceed}
                            variant="primary"
                            disabled={
                                isLoading || isError || !hasScrolledToBottom
                            }
                        >
                            <Trans
                                i18nKey={
                                    "termsOfService.modal.proceed.button.label"
                                }
                            ></Trans>
                        </Button>
                    </SpaceBetween>
                </Box>
            }
            header={<Trans i18nKey={"termsOfService.modal.header"} />}
        >
            {childComponent}
        </Modal>
    );
};

interface ErrorAlertProps {
    error: AWSError | undefined;
}

const ErrorAlert = (props: ErrorAlertProps) => {
    const { t } = useTranslation();
    return (
        <Alert
            statusIconAriaLabel={t("notification.error.statusIconAriaLabel")}
            type="error"
        >
            <SpaceBetween size="l" direction="vertical">
                <span data-testid="tos-modal-content-message">
                    <strong>
                        <Trans i18nKey="termsOfService.modal.content.error.general" />
                    </strong>
                </span>
                {props.error?.requestId && (
                    <ValueWithLabel
                        valueTestId={"tos-modal-error-requestid"}
                        label={t(
                            "termsOfService.modal.content.error.requestId.label"
                        )}
                    >
                        <CopyButton
                            copyText={props.error.requestId}
                            successPopoverText={t(
                                "termsOfService.modal.content.error.requestId.copy.popover.success"
                            )}
                            errorPopoverText={t(
                                "termsOfService.modal.content.error.requestId.copy.popover.failed"
                            )}
                            copyIconAriaLabel={t(
                                "termsOfService.modal.content.error.requestId.copy.button.ariaLabel"
                            )}
                        />
                        {props.error.requestId}
                    </ValueWithLabel>
                )}
            </SpaceBetween>
        </Alert>
    );
};
